/**
 * Global theme entry.
 *
 * Imports design tokens and base styles. Keep this minimal: per-block
 * behavior belongs in the block's own index.js, not here.
 */

import '../styles/tokens.css';
import '../styles/main.scss';
import './add-to-cart-toast';
import './wysiwyg-tables';

/**
 * Toggles the mobile navigation menu in the fixed site header.
 */
class SiteHeader {
	constructor(element) {
		this.element = element;
		this.toggle = element.querySelector('[data-header-toggle]');
		this.menu = element.querySelector('[data-header-menu]');
		this.searchToggle = element.querySelector('[data-search-toggle]');
		this.searchPanel = element.querySelector('[data-search-panel]');
		this.dropdowns = [...element.querySelectorAll('[data-dropdown]')];

		this.toggle.addEventListener('click', () => this.onToggle());
		this.menu.addEventListener('click', (event) => this.onMenuClick(event));
		this.searchToggle?.addEventListener('click', () => this.onSearchToggle());
		document.addEventListener('keydown', (event) => this.onKeydown(event));
		document.addEventListener('click', (event) => this.onDocumentClick(event));

		this.dropdowns.forEach((dropdown) => {
			const trigger = dropdown.querySelector('[data-dropdown-toggle]');
			if (!trigger) return;

			// Always a <button> now (never a link sharing double duty as a
			// toggle) — see inc/Nav_Walker.php — so no preventDefault() is
			// needed here; a real top-level link with children stays a
			// plain, fully-navigable <a> right next to this button.
			trigger.addEventListener('click', () => this.toggleDropdown(dropdown));
		});
	}

	onToggle() {
		const isOpen = this.element.classList.toggle('site-header--open');
		this.toggle.setAttribute('aria-expanded', String(isOpen));

		if (isOpen) {
			this.closeSearch();
		} else {
			this.closeAllDropdowns();
		}
	}

	// Its own toggle/panel, independent of the hamburger menu (data-header-toggle
	// /&__menu) — see header.php's docblock for why: a shopper searching
	// shouldn't have to open the full nav/account panel first.
	onSearchToggle() {
		if (!this.searchToggle || !this.searchPanel) return;

		const isOpen = this.searchPanel.classList.toggle('site-header__search-panel--open');
		this.searchToggle.setAttribute('aria-expanded', String(isOpen));

		if (isOpen) {
			this.close();
			this.searchPanel.querySelector('[data-header-search-input]')?.focus();
		}
	}

	closeSearch() {
		if (!this.searchToggle || !this.searchPanel) return;

		this.searchPanel.classList.remove('site-header__search-panel--open');
		this.searchToggle.setAttribute('aria-expanded', 'false');
	}

	onMenuClick(event) {
		const link = event.target.closest('a');
		if (link && !link.hasAttribute('data-dropdown-toggle')) {
			this.close();
		}
	}

	onDocumentClick(event) {
		if (!event.target.closest('[data-dropdown]')) {
			this.closeAllDropdowns();
		}
		if (!event.target.closest('[data-header]')) {
			this.close();
			this.closeSearch();
		}
	}

	onKeydown(event) {
		if (event.key === 'Escape') {
			const openDropdown = this.dropdowns.find(
				(d) =>
					d.querySelector('[data-dropdown-toggle]')?.getAttribute('aria-expanded') ===
					'true'
			);

			if (openDropdown) {
				this.closeDropdown(openDropdown);
				openDropdown.querySelector('[data-dropdown-toggle]')?.focus();
				return;
			}

			if (this.searchPanel?.classList.contains('site-header__search-panel--open')) {
				this.closeSearch();
				this.searchToggle?.focus();
				return;
			}

			this.close();
		}
	}

	toggleDropdown(dropdown) {
		const trigger = dropdown.querySelector('[data-dropdown-toggle]');
		const menu = dropdown.querySelector('[data-dropdown-menu]');
		const isOpen = trigger.getAttribute('aria-expanded') === 'true';

		this.closeAllDropdowns();

		if (!isOpen) {
			trigger.setAttribute('aria-expanded', 'true');
			menu.classList.add(`${menu.classList[0]}--open`);
		}
	}

	closeDropdown(dropdown) {
		const trigger = dropdown.querySelector('[data-dropdown-toggle]');
		const menu = dropdown.querySelector('[data-dropdown-menu]');
		trigger?.setAttribute('aria-expanded', 'false');
		menu?.classList.remove(`${menu.classList[0]}--open`);
	}

	closeAllDropdowns() {
		this.dropdowns.forEach((d) => this.closeDropdown(d));
	}

	close() {
		this.element.classList.remove('site-header--open');
		this.toggle.setAttribute('aria-expanded', 'false');
		this.closeAllDropdowns();
	}
}

const siteHeader = document.querySelector('[data-header]');

if (siteHeader?.querySelector('[data-header-toggle]')) {
	new SiteHeader(siteHeader);
}

/**
 * Keeps --header-height (src/styles/_header.scss) in sync with the header's
 * REAL rendered height instead of the hardcoded per-breakpoint guess it
 * ships as a first-paint fallback. The spacer below the fixed header (and
 * the error-page min-height) both key off that variable — any drift between
 * the guess and the actual height (a translation wrapping the menu
 * differently, a topbar link added/removed, a font metrics change) used to
 * leave a stray white gap between the fixed header and the page content
 * starting under it.
 */
if (siteHeader && 'ResizeObserver' in window) {
	const syncHeaderHeight = () => {
		document.documentElement.style.setProperty(
			'--header-height',
			`${siteHeader.offsetHeight}px`
		);
	};

	new ResizeObserver(syncHeaderHeight).observe(siteHeader);
	syncHeaderHeight();
}

/**
 * Keeps the header search submit button disabled while its input is empty —
 * an empty search has nowhere useful to go (see inc/product-search.php's
 * redirect_empty_search()), so it shouldn't be submittable in the first
 * place. Server-rendered disabled state (header.php) covers first paint;
 * this only keeps it in sync as the visitor types.
 */
document.querySelectorAll('[data-header-search]').forEach((form) => {
	const input = form.querySelector('[data-header-search-input]');
	const submit = form.querySelector('[data-header-search-submit]');

	if (!input || !submit) return;

	input.addEventListener('input', () => {
		submit.disabled = input.value.trim() === '';
	});
});

/**
 * Hides the topbar under the fixed navbar on scroll down, reveals it on
 * scroll up. The whole `.site-header-group` (topbar + navbar) translates by
 * the topbar's own height, so the navbar ends up pinned at the top.
 */
class TopBarScroll {
	constructor(group, topbar) {
		this.group = group;
		this.topbarHeight = topbar.offsetHeight;
		this.lastScrollY = window.scrollY;
		this.ticking = false;

		window.addEventListener('scroll', () => this.requestTick(), { passive: true });
	}

	requestTick() {
		if (this.ticking) return;
		this.ticking = true;
		window.requestAnimationFrame(() => this.onScroll());
	}

	onScroll() {
		const scrollY = window.scrollY;
		const scrollingDown = scrollY > this.lastScrollY;

		if (scrollY > this.topbarHeight && scrollingDown) {
			this.group.classList.add('site-header-group--topbar-hidden');
		} else if (!scrollingDown || scrollY <= this.topbarHeight) {
			this.group.classList.remove('site-header-group--topbar-hidden');
		}

		this.lastScrollY = scrollY;
		this.ticking = false;
	}
}

const headerGroup = document.querySelector('[data-header-group]');
const topbar = headerGroup?.querySelector('[data-topbar]');

if (headerGroup && topbar) {
	new TopBarScroll(headerGroup, topbar);
}

// Text slider — temporarily disabled together with its ACF field
// (inc/theme-options.php) and markup (header.php). To restore: uncomment
// this block.
/*
const topbarSwiperEl = topbar?.querySelector('[data-topbar-swiper]');

if (topbarSwiperEl && topbarSwiperEl.querySelectorAll('.swiper-slide').length > 1) {
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	(async () => {
		const [{ default: Swiper }, { A11y, Autoplay, Keyboard, Navigation }] = await Promise.all([
			import('swiper'),
			import('swiper/modules'),
			import('swiper/css'),
		]);

		// Slides render at their natural text width before Swiper touches them
		// (CSS forces `.site-topbar__message.swiper-slide { width: auto }`). Lock
		// the swiper root to the widest one, ONCE, before init — so slidesPerView:
		// 1 gives every slide that same fixed width instead of stretching to the
		// topbar's full width, and it never changes again while sliding.
		const slideWidths = [...topbarSwiperEl.querySelectorAll('.swiper-slide')].map(
			(slide) => slide.offsetWidth
		);
		topbarSwiperEl.style.width = `${Math.max(...slideWidths)}px`;

		new Swiper(topbarSwiperEl, {
			modules: [Navigation, Keyboard, Autoplay, A11y],
			slidesPerView: 1,
			loop: true,
			speed: prefersReducedMotion ? 0 : 500,
			keyboard: { enabled: true },
			a11y: { enabled: true },
			autoplay: prefersReducedMotion
				? false
				: { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true },
			navigation: {
				prevEl: topbar.querySelector('[data-topbar-prev]'),
				nextEl: topbar.querySelector('[data-topbar-next]'),
			},
		});
	})();
}
*/

/**
 * Entry animation for the guest CTA banner above the footer (logged-out
 * visitors only). Global site chrome, not a block, so it lives here rather
 * than in a block's own index.js.
 */
const guestCtaBlocks = document.querySelectorAll('.footer-guest-cta');
const guestCtaSwiperEls = document.querySelectorAll('[data-guest-cta-swiper]');

if (guestCtaSwiperEls.length) {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	(async () => {
		const [{ default: Swiper }, { A11y, Keyboard }] = await Promise.all([
			import('swiper'),
			import('swiper/modules'),
			import('swiper/css'),
		]);

		guestCtaSwiperEls.forEach((el) => {
			new Swiper(el, {
				modules: [Keyboard, A11y],
				slidesPerView: 1.5,
				spaceBetween: 16,
				speed: reduceMotion ? 0 : 500,
				keyboard: { enabled: true },
				a11y: { enabled: true },
				breakpoints: {
					768: { slidesPerView: 2.5, spaceBetween: 16 },
					1024: { slidesPerView: 4, spaceBetween: 16 },
				},
			});
		});
	})();
}

if (guestCtaBlocks.length) {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!reduceMotion) {
		guestCtaBlocks.forEach((block) => {
			if (block.getBoundingClientRect().bottom > 0) {
				block.dataset.animate = '';
			}
		});

		(async () => {
			const { gsap } = await import('gsap');
			const { ScrollTrigger } = await import('gsap/ScrollTrigger');

			gsap.registerPlugin(ScrollTrigger);

			document.querySelectorAll('.footer-guest-cta[data-animate]').forEach((block) => {
				const ctx = gsap.context(() => {
					const inner = block.querySelector('.footer-guest-cta__inner');
					const gallery = block.querySelector('.footer-guest-cta__gallery');

					if (inner) {
						gsap.to(inner, {
							opacity: 1,
							y: 0,
							duration: 0.8,
							ease: 'power3.out',
							scrollTrigger: {
								trigger: block,
								start: 'top 90%',
								once: true,
							},
						});
					}

					if (gallery) {
						gsap.to(gallery, {
							opacity: 1,
							y: 0,
							duration: 0.6,
							ease: 'power3.out',
							scrollTrigger: {
								trigger: block,
								start: 'top 90%',
								once: true,
							},
						});
					}
				}, block);

				block._gsapCtx = ctx;
			});
		})();
	}
}
